// client/src/components/react-dashboard/common/ConnectAtgDialog.jsx

import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    Step,
    StepLabel,
    Stepper,
    Tooltip,
    Typography
} from '@mui/material';

import {
    CheckCircleOutline,
    ContentCopy,
    RouterOutlined,
    StorageOutlined,
    WifiOutlined
} from '@mui/icons-material';

const ConnectAtgDialog = ({
    open,
    onClose,
    organizationId
}) => {
    const [copied, setCopied] = useState(false);

    const orgId = organizationId?.toString() || '';
    const validOrganizationId = /^\d{8}$/.test(orgId);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(orgId);
            setCopied(true);

            setTimeout(() => {
                setCopied(false);
            }, 2000);
        } catch (err) {
            console.error('Unable to copy organization ID:', err);
        }
    };

    const steps = [
        {
            label: 'Connect ATG Pro to Wi-Fi',
            icon: <WifiOutlined />,
            description:
                'Power on ATG Pro and use the touchscreen to connect the device to your site Wi-Fi network.'
        },
        {
            label: 'Enter your Organization ID',
            icon: <RouterOutlined />,
            description:
                'When prompted on ATG Pro, enter the 8-digit USFuel Organization ID shown below.'
        },
        {
            label: 'Enter site information',
            icon: <StorageOutlined />,
            description:
                'Give the device a recognizable site name and enter the physical address where the ATG is installed.'
        },
        {
            label: 'Connect your existing ATG',
            icon: <CheckCircleOutline />,
            description:
                'Connect ATG Pro to your existing automatic tank gauge and confirm the connection on the touchscreen. Once confirmed, ATG Pro will register with USFuel automatically.'
        }
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                Connect your ATG Pro
            </DialogTitle>

            <DialogContent>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                >
                    ATG Pro setup is completed directly from the device.
                    You only need your USFuel Organization ID from this dashboard.
                </Typography>

                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        mb: 3
                    }}
                >
                    <Typography
                        variant="overline"
                        color="text.secondary"
                    >
                        Your Organization ID
                    </Typography>

                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                    >
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                letterSpacing: '0.15em',
                                fontFamily: 'monospace'
                            }}
                        >
                            {orgId || 'Unavailable'}
                        </Typography>

                        {!!orgId && (
                            <Tooltip
                                title={copied ? 'Copied!' : 'Copy Organization ID'}
                            >
                                <IconButton onClick={handleCopy}>
                                    <ContentCopy />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>

                    {!validOrganizationId && orgId && (
                        <Alert
                            severity="warning"
                            sx={{ mt: 2 }}
                        >
                            This organization does not currently have a valid
                            8-digit numeric Organization ID.
                        </Alert>
                    )}
                </Paper>

                <Divider sx={{ mb: 3 }} />

                <Stepper
                    orientation="vertical"
                    activeStep={-1}
                >
                    {steps.map((step) => (
                        <Step
                            key={step.label}
                            active
                        >
                            <StepLabel
                                StepIconComponent={() => (
                                    <Box
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: '50%',
                                            bgcolor: 'primary.main',
                                            color: 'primary.contrastText',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        {step.icon}
                                    </Box>
                                )}
                            >
                                <Stack spacing={0.5}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                    >
                                        {step.label}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {step.description}
                                    </Typography>
                                </Stack>
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Alert
                    severity="success"
                    sx={{ mt: 3 }}
                >
                    Once setup is complete and ATG Pro checks in with USFuel,
                    the device will automatically appear in your Connected ATGs
                    list and begin sending tank information.
                </Alert>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={onClose}
                    variant="contained"
                >
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConnectAtgDialog;